import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useState, useEffect } from 'react';
import { isCancelledRequest, useApiError } from '../shared/hooks/api';

interface QueryProviderProps {
  children: ReactNode;
}

// Global error handler component
function GlobalErrorHandler({ queryClient }: { queryClient: QueryClient }) {
  const { handleError } = useApiError();

  useEffect(() => {
    // Set up global error handling for React Query
    const unsubscribeQuery = queryClient.getQueryCache().subscribe((event) => {
      // Background queries (e.g. polling) opt out of error toasts with `meta: { silentError: true }`
      if (
        event.type === 'observerResultsUpdated' &&
        event.query.state.error &&
        !event.query.meta?.silentError
      ) {
        handleError(event.query.state.error);
      }
    });

    // Mutations that show their own error messages opt out the same way.
    const unsubscribeMutation = queryClient.getMutationCache().subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.mutation.state.error &&
        !event.mutation.meta?.silentError
      ) {
        handleError(event.mutation.state.error);
      }
    });

    return () => {
      unsubscribeQuery();
      unsubscribeMutation();
    };
  }, [queryClient, handleError]);

  return null; // This component doesn't render anything
}

export const QueryProvider = ({ children }: QueryProviderProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 0, // 5 minutes
            retry: (failureCount, error: unknown) => {
              if (isCancelledRequest(error)) return false;
              // Don't retry on 401 errors (authentication errors)
              if ((error as { response?: { status?: number } })?.response?.status === 401) {
                return false;
              }
              // Retry up to 2 times for other errors
              return failureCount < 2;
            },
          },
          mutations: {
            retry: (failureCount, error: unknown) => {
              if (isCancelledRequest(error)) return false;
              // Don't retry on 401 errors
              if ((error as { response?: { status?: number } })?.response?.status === 401) {
                return false;
              }
              // Retry up to 2 times for other errors
              return failureCount < 2;
            },
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <GlobalErrorHandler queryClient={queryClient} />
      {children}
    </QueryClientProvider>
  );
};
